import type { Purse } from '@/queries/program_purses/schemas.ts'
import type { PurseAward } from './schema'

export const NO_AWARDED_STUDENTS_REASON =
  'None of the selected students have an award amount on this purse.' as const

export const INSUFFICIENT_FUNDS_REASON =
  'Available balance does not cover the awards for the selected students.' as const

export const NOT_ASSOCIATED_WITH_PURSE_NOTE =
  'Not associated with this purse, so it cannot fund this student.' as const

export const NO_AWARD_AMOUNT_NOTE =
  'No award amount was sent for this student on this purse.' as const

export type PurseAwardOption = {
  purse: Purse
  /** Total ServiceNow awarded to the selected students on this purse. */
  totalAmount: number
  /** How many of the selected students have an award amount on this purse. */
  studentsCount: number
  isSelectable: boolean
  /** Why the purse cannot be picked, or `null` when it can. */
  unavailableReason: string | null
}

/**
 * Prices every candidate purse against the award preview so purses that cannot fund this batch are
 * greyed out before the admin commits to one. A purse with no preview entry stays selectable — a
 * gap in the response must not read as "nothing to award".
 */
export function buildPurseAwardOptions({
  purses,
  purseAwards,
}: {
  purses: Purse[]
  purseAwards: PurseAward[] | undefined
}): PurseAwardOption[] {
  const awardByPurseId = new Map(
    (purseAwards ?? []).map((award) => [award.program_purse_id, award])
  )

  return purses.map((purse) => {
    const award = awardByPurseId.get(purse.id)
    const totalAmount = award?.total_amount ?? 0
    const studentsCount = award?.students.length ?? 0

    const unavailableReason = !award
      ? null
      : studentsCount === 0
        ? NO_AWARDED_STUDENTS_REASON
        : totalAmount > purse.balance_available
          ? INSUFFICIENT_FUNDS_REASON
          : null

    return {
      purse,
      totalAmount,
      studentsCount,
      isSelectable: unavailableReason === null,
      unavailableReason,
    }
  })
}

/**
 * The reason a student cannot be funded from the selected purse, or `null` when they can. Drives
 * both the per student disclaimer and the disabled Next button on the review step.
 *
 * An amount in the preview settles it: the API only prices a student against a purse they hold an
 * awarded amount on, so it outranks the purse associations on the application record, which can lag.
 */
export function getStudentAwardBlockerNote({
  isAssociatedWithPurse,
  hasAwardAmount,
}: {
  isAssociatedWithPurse: boolean
  hasAwardAmount: boolean
}): string | null {
  if (hasAwardAmount) {
    return null
  }

  if (!isAssociatedWithPurse) {
    return NOT_ASSOCIATED_WITH_PURSE_NOTE
  }

  return NO_AWARD_AMOUNT_NOTE
}
